import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Swal from 'sweetalert2';

export default function SettingsMedia() {
    const [sizes, setSizes] = useState<any[]>([]);
    const [thumbs, setThumbs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Edit Size State
    const [editingSize, setEditingSize] = useState<any>(null);
    const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);
    const [sizeForm, setSizeForm] = useState({ width: 0, height: 0 });

    // Edit Thumb State
    const [editingThumbType, setEditingThumbType] = useState<string | null>(null); // 'audio', 'video', 'document'
    const [isThumbModalOpen, setIsThumbModalOpen] = useState(false);
    const [thumbFile, setThumbFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [sizesRes, thumbsRes] = await Promise.all([
                client.getApiMediaSettingsSizes() as any,
                client.getApiMediaThumbThumbs() as any
            ]);

            const sizesData = sizesRes.data?.data || sizesRes.data || [];
            const thumbsData = thumbsRes.data?.data || thumbsRes.data || [];

            setSizes(Array.isArray(sizesData) ? sizesData : []);
            setThumbs(Array.isArray(thumbsData) ? thumbsData : []);
        } catch (err) {
            console.error('Failed to fetch media settings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Image Sizes Handlers ---

    const handleEditSize = (item: any) => {
        setEditingSize(item);
        setSizeForm({
            width: item.width || 0,
            height: item.height || 0
        });
        setIsSizeModalOpen(true);
    };

    const handleSaveSize = async () => {
        if (!editingSize) return;
        try {
            await client.postApiMediaSettingsSizeUpdateId(
                { id: editingSize.id },
                {
                    data: {
                        width: Number(sizeForm.width),
                        height: Number(sizeForm.height)
                    }
                } as any
            );
            setIsSizeModalOpen(false);
            setEditingSize(null);
            fetchData();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Size updated successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err) {
            console.error('Failed to update size:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to update size',
                icon: 'error'
            });
        }
    };

    // --- Default Thumbs Handlers ---

    const handleEditThumb = (type: string) => {
        setEditingThumbType(type);
        setThumbFile(null);
        setIsThumbModalOpen(true);
    };

    const handleSaveThumb = async () => {
        if (!editingThumbType || !thumbFile) return;
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', thumbFile);

            if (editingThumbType === 'audio') {
                await client.postApiMediaThumbUpdateAudio({
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                } as any);
            } else if (editingThumbType === 'video') {
                await client.postApiMediaThumbUpdateVideo({
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                } as any);
            } else if (editingThumbType === 'document') {
                await client.postApiMediaThumbUpdateDocument({
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                } as any);
            }

            setIsThumbModalOpen(false);
            setEditingThumbType(null);
            setThumbFile(null);
            fetchData();

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Thumbnail updated successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err: any) {
            console.error(`Failed to update ${editingThumbType} thumb:`, err);
            Swal.fire({
                title: 'Error',
                text: `Failed to update thumbnail: ${err.message || 'Unknown error'}`,
                icon: 'error'
            });
        } finally {
            setUploading(false);
        }
    };

    const getThumbUrl = (item: any) => {
        if (!item) return '';
        const path = item.thumb_m || item.path || '';
        if (!path) return '';
        if (path.startsWith('http')) return path;

        const baseUrl = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');

        // Handle paths starting with uploads/ or thumbs/
        if (path.startsWith('uploads/') || path.startsWith('thumbs/')) {
            return `${baseUrl}/${path}`;
        }

        return `${baseUrl}/${path.replace(/^\//, '').replace(/^storage\//, '')}`;
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Image Sizes Section */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-white/[0.02]">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Image Sizes</h3>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Define the dimensions for auto-generated thumbnails.</p>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Width</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Height</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && sizes.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">
                                    <div className="flex justify-center items-center gap-2 text-gray-400">
                                        <div className="w-4 h-4 border-2 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
                                        Loading dimensions...
                                    </div>
                                </TableCell></TableRow>
                            ) : sizes.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center text-gray-500 italic">No sizes found.</TableCell></TableRow>
                            ) : (
                                sizes.map(item => (
                                    <TableRow key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.01]">
                                        <TableCell className="px-5 py-4 font-bold text-gray-700 dark:text-gray-200 capitalize">{item.name}</TableCell>
                                        <TableCell className="px-5 py-4 font-mono text-sm">{item.width} px</TableCell>
                                        <TableCell className="px-5 py-4 font-mono text-sm">{item.height} px</TableCell>
                                        <TableCell className="px-5 py-4 text-end">
                                            <button
                                                onClick={() => handleEditSize(item)}
                                                className="p-2 text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 rounded-lg transition-colors"
                                                title="Edit Dimensions"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Default Thumbs Section */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-white/[0.02]">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Default Thumbnails</h3>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Set default preview images for non-image files.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 p-6 bg-gray-50/30 dark:bg-black/10">
                    {/* Audio */}
                    <ThumbCard
                        type="Audio"
                        icon="🎵"
                        url={getThumbUrl(thumbs.find(t => t.id === 3 || t.type === 'audio'))}
                        onEdit={() => handleEditThumb('audio')}
                    />

                    {/* Video */}
                    <ThumbCard
                        type="Video"
                        icon="🎬"
                        url={getThumbUrl(thumbs.find(t => t.id === 2 || t.type === 'video'))}
                        onEdit={() => handleEditThumb('video')}
                    />

                    {/* Document */}
                    <ThumbCard
                        type="Document"
                        icon="📄"
                        url={getThumbUrl(thumbs.find(t => t.id === 1 || t.type === 'doc' || t.type === 'document'))}
                        onEdit={() => handleEditThumb('document')}
                    />
                </div>
            </div>

            {/* Edit Size Modal */}
            <Modal isOpen={isSizeModalOpen} onClose={() => setIsSizeModalOpen(false)} className="max-w-md p-0 overflow-hidden">
                <div className="flex flex-col h-full bg-white dark:bg-gray-900">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Edit Size: {editingSize?.key || editingSize?.name}</h3>
                    </div>

                    <div className="p-6 overflow-y-auto">
                        <form id="size-form" onSubmit={(e) => { e.preventDefault(); handleSaveSize(); }} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Width (px)</label>
                                <input
                                    type="number"
                                    value={sizeForm.width}
                                    onChange={e => setSizeForm({ ...sizeForm, width: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Height (px)</label>
                                <input
                                    type="number"
                                    value={sizeForm.height}
                                    onChange={e => setSizeForm({ ...sizeForm, height: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono"
                                />
                            </div>
                        </form>
                    </div>

                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsSizeModalOpen(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="size-form"
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Edit Thumb Modal */}
            <Modal isOpen={isThumbModalOpen} onClose={() => setIsThumbModalOpen(false)} className="max-w-md p-0 overflow-hidden">
                <div className="flex flex-col h-full bg-white dark:bg-gray-900">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tight">Update {editingThumbType} Thumbnail</h3>
                    </div>

                    <div className="p-6 overflow-y-auto">
                        <form id="thumb-form" onSubmit={(e) => { e.preventDefault(); handleSaveThumb(); }} className="space-y-4">
                            <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl p-10 text-center bg-gray-50 dark:bg-gray-800/20 hover:border-brand-500/50 transition-colors group">
                                <input
                                    type="file"
                                    id="thumb-upload"
                                    accept="image/*"
                                    onChange={e => setThumbFile(e.target.files?.[0] || null)}
                                    className="hidden"
                                />
                                <label htmlFor="thumb-upload" className="cursor-pointer flex flex-col items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center text-brand-500 group-hover:scale-110 transition-transform">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-bold text-gray-600 dark:text-gray-400">Click to upload or drag & drop</span>
                                    <span className="text-xs text-gray-400 font-medium">Recommended: Square PNG/JPEG (min 256x256)</span>
                                </label>
                            </div>
                            {thumbFile && (
                                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/20 rounded-xl">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <p className="text-sm font-bold text-green-700 dark:text-green-400 truncate flex-grow">Selected: {thumbFile.name}</p>
                                    <button type="button" onClick={() => setThumbFile(null)} className="text-red-500 hover:text-red-700 p-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>

                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsThumbModalOpen(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="thumb-form"
                            disabled={!thumbFile || uploading}
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 disabled:opacity-50 flex items-center gap-2 transition-all active:scale-95"
                        >
                            {uploading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                    Uploading...
                                </>
                            ) : 'Upload & Save'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

function ThumbCard({ type, icon, url, onEdit }: { type: string, icon: string, url?: string, onEdit: () => void }) {
    return (
        <div className="flex flex-col items-center gap-4 p-6 border rounded-2xl border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 group hover:border-brand-500/30 transition-all shadow-sm">
            <span className="font-bold text-gray-400 dark:text-gray-500 uppercase text-xs tracking-widest">{type}</span>
            <div className="relative w-32 h-32 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center overflow-hidden border border-gray-100 dark:border-gray-800 group-hover:scale-105 transition-transform">
                {url ? (
                    <img src={url} alt={`${type} Thumb`} className="w-full h-full object-cover" />
                ) : (
                    <span className="text-4xl filter grayscale group-hover:grayscale-0 transition-all">{icon}</span>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-bold px-2 py-1 bg-white/20 backdrop-blur-md rounded-lg">Preview</span>
                </div>
            </div>
            <button
                onClick={onEdit}
                className="w-full py-2 px-4 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
                Change Thumbnail
            </button>
        </div>
    );
}
